import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Send, AlertTriangle, Package, Download, Pencil } from 'lucide-react';
import { useMesBcas, useSoumettreBca } from '@/hooks/useAchats';
import { achatsApi, type Bca } from '@/api/achats';
import { toast } from '@/stores/toastStore';
import { Badge, type BadgeTone } from './_ui';

const STATUS_LABELS: Record<string, { label: string; tone: BadgeTone }> = {
  brouillon:           { label: 'Brouillon',       tone: 'neutral' },
  en_validation_daf:   { label: 'Chez le DAF',     tone: 'info' },
  emis:                { label: 'Envoyé',          tone: 'success' },
  reception_partielle: { label: 'Récep. partielle', tone: 'info' },
  recu_complet:        { label: 'Reçu complet',    tone: 'success' },
  annule:              { label: 'Annulé',          tone: 'danger' },
};

export const MesBcasPage = () => {
  const [sp] = useSearchParams();
  const filtre = sp.get('statut');

  const statutBackend = filtre === 'renvoyes' ? 'brouillon' : filtre ?? undefined;
  const { data = [], isLoading } = useMesBcas(statutBackend);

  const rows = useMemo(() => {
    if (filtre === 'renvoyes')  return data.filter((b) => b.renvoyeeMotif !== null);
    if (filtre === 'brouillon') return data.filter((b) => b.renvoyeeMotif === null);
    return data;
  }, [data, filtre]);

  return (
    <div>
      <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>Mes bons de commande</h1>
      <p style={{ margin: '0 0 16px', color: 'var(--tx-3, #6B7280)', fontSize: 13 }}>{filtreLabel(filtre)}</p>

      {isLoading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div>}

      {!isLoading && rows.length === 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--bd-1)', borderRadius: 12, padding: 32, textAlign: 'center' }}>
          <Package size={40} style={{ color: 'var(--tx-3)', marginBottom: 8 }} />
          <p style={{ color: 'var(--tx-3)', margin: 0, fontSize: 14 }}>Aucun BCA dans ce filtre.</p>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {rows.map((b) => <BcaCard key={b.id} bca={b} />)}
      </div>
    </div>
  );
};

function filtreLabel(f: string | null): string {
  if (f === 'renvoyes')          return 'Renvoyés par le DAF — à corriger';
  if (f === 'brouillon')         return 'Brouillons — à finaliser et soumettre';
  if (f === 'en_validation_daf') return 'En attente de validation DAF';
  if (f === 'emis')              return 'Envoyés au fournisseur — attente livraison';
  return 'Tous tes BCA récents';
}

function BcaCard({ bca: b }: { bca: Bca }) {
  const nav = useNavigate();
  const soumettre = useSoumettreBca();
  const [downloading, setDownloading] = useState(false);
  const stat = STATUS_LABELS[b.statut] ?? { label: b.statut, tone: 'neutral' as const };
  const ht  = Number(BigInt(b.montantHtCents.toString())) / 100;
  const ligneCount = b.lignes?.length ?? 0;
  const modifiable = b.statut === 'brouillon' || b.statut === 'en_validation_daf';

  const telecharger = async () => {
    setDownloading(true);
    try {
      await achatsApi.telechargerPdf(b.id, b.reference);
      toast.success('PDF téléchargé', b.reference);
    } catch {
      // Toast déjà émis par le décodeur blob dans achatsApi
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{
      background: '#fff', border: '1px solid var(--bd-1)', borderRadius: 12,
      padding: 14, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{b.reference}</div>
          <div style={{ fontSize: 13, color: 'var(--tx-2)' }}>{b.fournisseur?.raisonSociale ?? '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 2 }}>
            {ligneCount} MP · Créé le {new Date(b.createdAt).toLocaleDateString('fr-FR')}
          </div>
        </div>
        <Badge tone={stat.tone}>{stat.label}</Badge>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>Total HT</div>
        <div style={{ fontWeight: 800, fontSize: 18, fontFamily: 'monospace' }}>
          {ht.toLocaleString('fr-FR')} FCFA
        </div>
      </div>

      {b.renvoyeeMotif && (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
          padding: 10, fontSize: 12, color: '#991B1B',
          display: 'flex', gap: 6, alignItems: 'flex-start',
        }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>
              Renvoyé par le DAF — {b.renvoyeeLe && new Date(b.renvoyeeLe).toLocaleDateString('fr-FR')}
            </div>
            <div>{b.renvoyeeMotif}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
        {/* PDF dispo à tous les statuts */}
        <button
          onClick={telecharger}
          disabled={downloading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 12px', borderRadius: 8, border: '1px solid var(--bd-1)',
            background: '#fff', color: 'var(--tx-2)', fontSize: 13, cursor: 'pointer',
          }}
        >
          <Download size={13} /> {downloading ? 'Génération…' : 'Télécharger PDF'}
        </button>

        {/* Modification autorisée en brouillon OU en_validation_daf (gèle après emis) */}
        {modifiable && (
          <button
            onClick={() => nav(`/achats/nouveau?edit=${b.id}`)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 12px', borderRadius: 8, border: '1px solid var(--bd-1)',
              background: '#fff', color: 'var(--tx-2)', fontSize: 13, cursor: 'pointer',
            }}
          >
            <Pencil size={13} /> Modifier
          </button>
        )}

        {b.statut === 'brouillon' && (
          <button
            onClick={() => soumettre.mutate(b.id)}
            disabled={soumettre.isPending}
            style={{
              flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '10px 12px', borderRadius: 8, border: 'none',
              background: '#0F766E', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
            }}
          >
            <Send size={14} /> Soumettre au DAF
          </button>
        )}
      </div>
    </div>
  );
}
